import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageWithFallback } from '@/components/figma/ImageWithFallback';
import { cn } from '@/components/ui/utils';
import type { ClientCalendarSearchEvent } from '@/api/beatlyApi';
import { useClientCalendarSearch } from '@/hooks/useClientCalendarSearch';
import { getHrefForCalendarEvent } from '@/utils/calendarEventNavigation';

/** Campaign event colors; green slot matches Content Approval “Accepted” / success emerald */
const CAMPAIGN_COLOR_PALETTE = ['#be43ff', '#00b1a5', '#f59e0b', '#10b981', '#3b82f6', '#ff5635'] as const;
const UNKNOWN_CAMPAIGN_COLOR = '#667085';

function parseYmd(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${month}-${day}`;
}

/** ISO range for API: first instant of window start through last instant of window end (local). */
function windowToApiRange(months: { year: number; month: number }[]): { dateFrom: string; dateTo: string } {
  const first = months[0];
  const last = months[months.length - 1];
  const from = new Date(first.year, first.month, 1, 0, 0, 0, 0);
  const to = new Date(last.year, last.month + 1, 0, 23, 59, 59, 999);
  return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
}

/** Four calendar months: (current month − 1) + offset, then +1, +2, +3. */
function getWindowMonths(calendarOffset: number): { year: number; month: number }[] {
  const anchor = new Date();
  anchor.setDate(1);
  anchor.setMonth(anchor.getMonth() - 1 + calendarOffset);
  const out: { year: number; month: number }[] = [];
  for (let i = 0; i < 4; i += 1) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() + i, 1);
    out.push({ year: d.getFullYear(), month: d.getMonth() });
  }
  return out;
}

function leadingEmptyCellsMondayFirst(year: number, month: number): number {
  const dow = new Date(year, month, 1).getDay();
  return (dow + 6) % 7;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function buildMonthDayCells(year: number, month: number): (number | null)[] {
  const leading = leadingEmptyCellsMondayFirst(year, month);
  const dim = daysInMonth(year, month);
  const cells: (number | null)[] = [
    ...Array.from({ length: leading }, () => null),
    ...Array.from({ length: dim }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
}

function formatEventTimeLabel(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
    return null;
  }
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function hexWithAlpha(hex: string, aa: string): string {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  return `#${h}${aa}`;
}

function creatorInitials(name: string | null | undefined, handle: string | null | undefined): string {
  const n = (name ?? '').trim();
  if (n.length > 0) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase() || '?';
    }
    return n.slice(0, 2).toUpperCase() || '?';
  }
  const h = (handle ?? '').replace(/^@/, '').trim();
  if (h.length > 0) return h.slice(0, 2).toUpperCase();
  return '?';
}

function scrollChildToTop(container: HTMLElement, child: HTMLElement) {
  const cRect = container.getBoundingClientRect();
  const eRect = child.getBoundingClientRect();
  container.scrollTop += eRect.top - cRect.top;
}

type CampaignChip = { id: string; name: string; color: string };

type UiCalendarEvent = ClientCalendarSearchEvent & {
  timeLabel: string | null;
  campaignKey: string;
  accentColor: string;
};

type UiCollaborationSummary = {
  collaboration_id: string;
  creator_name: string | null;
  creator_handle: string | null;
  creator_mugshot_url: string | null;
  count: number;
};

type UiEventGroup = {
  key: string;
  events: UiCalendarEvent[];
  representative: UiCalendarEvent;
  count: number;
  /** Non-empty when events are bundled by beat (one card, multiple creators). */
  collaborations: UiCollaborationSummary[];
};

/** Drop duplicate API rows with the same event_id (keeps first occurrence). */
function dedupeDayEventsByEventId(dayEvents: UiCalendarEvent[]): UiCalendarEvent[] {
  const seen = new Set<string>();
  const out: UiCalendarEvent[] = [];
  for (const ev of dayEvents) {
    const id = (ev.event_id ?? '').trim();
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(ev);
  }
  return out;
}

/** One summary per collaboration_id from bundled beat events (sorted by creator display name). */
function buildCollaborationSummariesFromEvents(events: UiCalendarEvent[]): UiCollaborationSummary[] {
  const byCollab = new Map<string, UiCalendarEvent[]>();
  for (const ev of events) {
    const cid = (ev.collaboration_id ?? '').trim();
    if (!cid) continue;
    const list = byCollab.get(cid);
    if (list) list.push(ev);
    else byCollab.set(cid, [ev]);
  }
  const summaries: UiCollaborationSummary[] = [];
  for (const [collabId, evs] of byCollab) {
    const sorted = [...evs].sort((a, b) => a.event_id.localeCompare(b.event_id));
    const rep = sorted[0]!;
    summaries.push({
      collaboration_id: collabId,
      creator_name: rep.creator_name ?? null,
      creator_handle: rep.creator_handle ?? null,
      creator_mugshot_url: rep.creator_mugshot_url ?? null,
      count: sorted.length,
    });
  }
  summaries.sort((a, b) => {
    const nameA = (a.creator_name ?? '').trim() || (a.creator_handle ?? '').replace(/^@/, '').trim();
    const nameB = (b.creator_name ?? '').trim() || (b.creator_handle ?? '').replace(/^@/, '').trim();
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
  });
  return summaries;
}

/**
 * Bundle collaboration events by type + beat + date_start + campaign; collab-only (no beat_id)
 * keeps type + collaboration + date_start with legacy single-card UI.
 */
function groupDayEvents(dayEvents: UiCalendarEvent[]): UiEventGroup[] {
  const uniqueDayEvents = dedupeDayEventsByEventId(dayEvents);
  const beatMergeable = new Map<string, UiCalendarEvent[]>();
  const collabOnlyMergeable = new Map<string, UiCalendarEvent[]>();
  const singles: UiCalendarEvent[] = [];

  for (const ev of uniqueDayEvents) {
    const collab = (ev.collaboration_id ?? '').trim();
    if (!collab) {
      singles.push(ev);
      continue;
    }
    const beat = (ev.beat_id ?? '').trim();
    const ds = (ev.date_start ?? '').trim();
    if (beat) {
      const camp = (ev.campaign_id ?? '').trim() || '__none__';
      const gkey = `${ev.type}\0${beat}\0${ds}\0${camp}`;
      const list = beatMergeable.get(gkey);
      if (list) list.push(ev);
      else beatMergeable.set(gkey, [ev]);
    } else {
      const gkey = `${ev.type}\0${collab}\0${ds}`;
      const list = collabOnlyMergeable.get(gkey);
      if (list) list.push(ev);
      else collabOnlyMergeable.set(gkey, [ev]);
    }
  }

  const groups: UiEventGroup[] = [];

  for (const ev of singles) {
    groups.push({
      key: ev.event_id,
      events: [ev],
      representative: ev,
      count: 1,
      collaborations: [],
    });
  }

  for (const [gkey, events] of beatMergeable) {
    const sorted = [...events].sort((a, b) => a.event_id.localeCompare(b.event_id));
    groups.push({
      key: gkey,
      events: sorted,
      representative: sorted[0]!,
      count: sorted.length,
      collaborations: buildCollaborationSummariesFromEvents(sorted),
    });
  }

  for (const [gkey, events] of collabOnlyMergeable) {
    const sorted = [...events].sort((a, b) => a.event_id.localeCompare(b.event_id));
    groups.push({
      key: gkey,
      events: sorted,
      representative: sorted[0]!,
      count: sorted.length,
      collaborations: [],
    });
  }

  groups.sort((a, b) => {
    const cmp = (a.representative.date_start ?? '').localeCompare(b.representative.date_start ?? '');
    if (cmp !== 0) return cmp;
    return a.representative.event_id.localeCompare(b.representative.event_id);
  });

  return groups;
}

function getCalendarEventCardAriaLabel(
  event: UiCalendarEvent,
  translate: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (!getHrefForCalendarEvent(event)) return null;
  let sectionKey = 'campaignCalendar.navSectionOverview';
  if (event.type === 'DRAFT_DEADLINE') {
    sectionKey = 'campaignCalendar.navSectionDrafts';
  } else if ((event.collaboration_id ?? '').trim()) {
    sectionKey = 'campaignCalendar.navSectionCollaborations';
  }
  const campaign = (event.campaign_name ?? '').trim() || translate('campaignCalendar.openEventCampaignFallback');
  return translate('campaignCalendar.openEventCardAria', {
    section: translate(sectionKey),
    campaign,
  });
}

export function CampaignCalendarPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const calendarCardRef = useRef<HTMLDivElement>(null);
  const [calendarOffset, setCalendarOffset] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('all');
  const [eventsPanelMaxHeightPx, setEventsPanelMaxHeightPx] = useState<number | null>(null);
  const [isLgViewport, setIsLgViewport] = useState(false);

  const windowMonths = useMemo(() => getWindowMonths(calendarOffset), [calendarOffset]);
  const { dateFrom, dateTo } = useMemo(() => windowToApiRange(windowMonths), [windowMonths]);

  const { data, isLoading, isError, error, isFetching } = useClientCalendarSearch(dateFrom, dateTo);

  const rawEvents = data?.events ?? [];

  const campaignChips: CampaignChip[] = useMemo(() => {
    const map = new Map<string, string>();
    for (const ev of rawEvents) {
      const id = (ev.campaign_id ?? '').trim() || '__none__';
      const name = (ev.campaign_name ?? ev.brand_name ?? id).trim() || id;
      if (!map.has(id)) map.set(id, name);
    }
    const sortedIds = [...map.keys()].sort();
    const colored: CampaignChip[] = sortedIds.map((id, idx) => ({
      id,
      name: map.get(id) ?? id,
      color: id === '__none__' ? UNKNOWN_CAMPAIGN_COLOR : CAMPAIGN_COLOR_PALETTE[idx % CAMPAIGN_COLOR_PALETTE.length],
    }));
    return [{ id: 'all', name: t('campaignCalendar.campaigns.all'), color: '#667085' }, ...colored];
  }, [rawEvents, t]);

  const colorByCampaignId = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of campaignChips) {
      if (c.id !== 'all') m.set(c.id, c.color);
    }
    return m;
  }, [campaignChips]);

  const uiEvents: UiCalendarEvent[] = useMemo(() => {
    return rawEvents.map((ev) => {
      const key = (ev.campaign_id ?? '').trim() || '__none__';
      const accent = colorByCampaignId.get(key) ?? UNKNOWN_CAMPAIGN_COLOR;
      return {
        ...ev,
        timeLabel: formatEventTimeLabel(ev.date_start),
        campaignKey: key,
        accentColor: accent,
      };
    });
  }, [rawEvents, colorByCampaignId]);

  const filteredUiEvents = useMemo(() => {
    if (selectedCampaign === 'all') return uiEvents;
    return uiEvents.filter((e) => e.campaignKey === selectedCampaign);
  }, [uiEvents, selectedCampaign]);

  useEffect(() => {
    if (selectedCampaign === 'all') return;
    const exists = campaignChips.some((c) => c.id === selectedCampaign);
    if (!exists) setSelectedCampaign('all');
  }, [campaignChips, selectedCampaign]);

  const todayYmd = formatLocalYmd(new Date());

  const getDayDistance = (dateStr: string) => {
    const eventDate = parseYmd(dateStr);
    const today = parseYmd(todayYmd);
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('campaignCalendar.relative.today');
    if (diffDays === 1) return t('campaignCalendar.relative.tomorrow');
    if (diffDays === -1) return t('campaignCalendar.relative.yesterday');
    if (diffDays > 1) return t('campaignCalendar.relative.inDays', { count: diffDays });
    if (diffDays < -1) return t('campaignCalendar.relative.daysAgo', { count: Math.abs(diffDays) });
    return dateStr;
  };

  const eventsByDate = useMemo(
    () =>
      filteredUiEvents.reduce(
        (acc, event) => {
          const raw = (event.date ?? '').trim();
          if (!raw) return acc;
          const d = raw.slice(0, 10);
          if (!acc[d]) acc[d] = [];
          acc[d].push(event);
          return acc;
        },
        {} as Record<string, UiCalendarEvent[]>,
      ),
    [filteredUiEvents],
  );

  const sortedDates = useMemo(() => Object.keys(eventsByDate).sort(), [eventsByDate]);

  const groupedEventsByDate = useMemo(() => {
    const out: Record<string, UiEventGroup[]> = {};
    for (const date of Object.keys(eventsByDate)) {
      out[date] = groupDayEvents(eventsByDate[date]!);
    }
    return out;
  }, [eventsByDate]);

  const scrollAnchorDate = useMemo(() => {
    if (sortedDates.length === 0) return null;
    const onOrAfter = sortedDates.find((d) => d >= todayYmd);
    if (onOrAfter) return onOrAfter;
    return sortedDates[sortedDates.length - 1];
  }, [sortedDates, todayYmd]);

  useLayoutEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const updateLg = () => setIsLgViewport(mq.matches);
    updateLg();
    mq.addEventListener('change', updateLg);
    return () => mq.removeEventListener('change', updateLg);
  }, []);

  useLayoutEffect(() => {
    const el = calendarCardRef.current;
    if (!el || !isLgViewport) {
      setEventsPanelMaxHeightPx(null);
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (h > 0) setEventsPanelMaxHeightPx(h * 2);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isLgViewport]);

  useLayoutEffect(() => {
    const container = eventsScrollRef.current;
    if (!container || !scrollAnchorDate || isLoading) return;
    const child = container.querySelector(`[data-date="${scrollAnchorDate}"]`) as HTMLElement | null;
    if (!child) return;
    requestAnimationFrame(() => {
      scrollChildToTop(container, child);
    });
  }, [scrollAnchorDate, selectedCampaign, isLoading, sortedDates.length, data?.events?.length]);

  const weekdayLabels = [
    t('campaignCalendar.weekdays.mon'),
    t('campaignCalendar.weekdays.tue'),
    t('campaignCalendar.weekdays.wed'),
    t('campaignCalendar.weekdays.thu'),
    t('campaignCalendar.weekdays.fri'),
    t('campaignCalendar.weekdays.sat'),
    t('campaignCalendar.weekdays.sun'),
  ];

  const selectedFilterName =
    selectedCampaign === 'all'
      ? ''
      : campaignChips.find((c) => c.id === selectedCampaign)?.name ?? '';

  const showLoading = isLoading || (isFetching && !data);

  function scrollToDateInList(dateStr: string) {
    if (!eventsScrollRef.current) return;
    const eventElement = eventsScrollRef.current.querySelector(`[data-date="${dateStr}"]`);
    if (eventElement) {
      eventElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function renderMonthGrid(year: number, month: number) {
    const cells = buildMonthDayCells(year, month);
    const monthTitle = new Date(year, month, 1).toLocaleString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });

    return (
      <div key={`${year}-${month}`}>
        <h6 className="text-[13px] font-bold text-[#101828] mb-3">{monthTitle}</h6>
        <div className="grid grid-cols-7 gap-[3px]">
          {weekdayLabels.map((day, i) => (
            <div key={i} className="text-center text-[9px] font-bold text-[#98a2b3] py-0.5">
              {day}
            </div>
          ))}
          {cells.map((day, i) => {
            const dateStr = day ? formatLocalYmd(new Date(year, month, day)) : null;
            const hasEvent = Boolean(dateStr && eventsByDate[dateStr]);
            const isToday = dateStr !== null && dateStr === todayYmd;
            const dayAriaLabel =
              day != null
                ? (() => {
                    const full = new Date(year, month, day).toLocaleDateString(i18n.language, {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    });
                    return hasEvent
                      ? t('campaignCalendar.dayButtonHasEventsAria', { date: full })
                      : t('campaignCalendar.dayButtonAria', { date: full });
                  })()
                : undefined;

            return (
              <button
                key={i}
                type="button"
                aria-label={dayAriaLabel}
                aria-current={isToday ? 'date' : undefined}
                onClick={() => {
                  if (dateStr && hasEvent) scrollToDateInList(dateStr);
                }}
                className={cn(
                  'w-7 h-7 rounded-lg text-[11px] font-bold transition-all',
                  !day && 'invisible',
                  day && isToday && 'bg-[#be43ff] text-white shadow-md',
                  day && !isToday && hasEvent &&
                    'bg-white text-[#be43ff] hover:bg-white/90 relative cursor-pointer ring-1 ring-[#be43ff]/20 shadow-sm',
                  day && !isToday && !hasEvent && 'text-[#667085] hover:bg-gray-50',
                )}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[var(--background)] p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-[28px] text-[#101828] mb-2" style={{ fontWeight: 700 }}>
            {t('campaignCalendar.title')}
          </h1>
          <p className="text-[14px] text-[#667085]" style={{ fontWeight: 500 }}>
            {t('campaignCalendar.subtitle')}
          </p>
        </div>

        <div className="mb-6">
          <Card className="bg-white border-0 shadow-sm rounded-[24px] overflow-hidden">
            <CardContent className="p-6">
              {showLoading ? (
                <div className="h-10 max-w-xl animate-pulse rounded-lg bg-gray-100" aria-hidden />
              ) : isError ? (
                <p className="text-sm text-destructive" role="alert">
                  {t('campaignCalendar.loadError')}
                  {error instanceof Error ? `: ${error.message}` : ''}
                </p>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-[#667085] mr-2" style={{ fontWeight: 600 }}>
                    {t('campaignCalendar.filterByCampaign')}
                  </span>
                  {campaignChips.map((campaign) => (
                    <button
                      key={campaign.id}
                      type="button"
                      onClick={() => setSelectedCampaign(campaign.id)}
                      className={cn(
                        'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] transition-all',
                        selectedCampaign === campaign.id
                          ? 'bg-[#be43ff] text-white shadow-sm'
                          : 'bg-gray-50 text-[#667085] hover:bg-gray-100',
                      )}
                      style={{ fontWeight: 600 }}
                    >
                      {selectedCampaign === campaign.id && <Check className="w-3.5 h-3.5" aria-hidden />}
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: campaign.color,
                          opacity: selectedCampaign === campaign.id ? 1 : 0.6,
                        }}
                      />
                      {campaign.name}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div ref={calendarCardRef}>
              <Card className="bg-white border-0 shadow-sm rounded-[24px] overflow-hidden">
                <CardHeader className="px-8 pt-8 pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-[18px] font-bold text-[#101828]">
                      {t('campaignCalendar.calendarTitle')}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="px-3 py-1 rounded-xl text-[12px] text-[#667085] hover:bg-gray-100 transition-colors"
                        style={{ fontWeight: 600 }}
                        onClick={() => setCalendarOffset(0)}
                      >
                        {new Date().toLocaleString(i18n.language, { month: 'long', year: 'numeric' })}
                      </button>
                      <button
                        type="button"
                        className="w-8 h-8 rounded-xl hover:bg-gray-100 flex items-center justify-center transition-colors"
                        aria-label={t('campaignCalendar.prevMonthAria')}
                        onClick={() => setCalendarOffset((o) => o - 1)}
                      >
                        <ChevronLeft className="w-4 h-4 text-[#667085]" />
                      </button>
                      <button
                        type="button"
                        className="w-8 h-8 rounded-xl hover:bg-gray-100 flex items-center justify-center transition-colors"
                        aria-label={t('campaignCalendar.nextMonthAria')}
                        onClick={() => setCalendarOffset((o) => o + 1)}
                      >
                        <ChevronRight className="w-4 h-4 text-[#667085]" />
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-8 pb-8">
                  {showLoading ? (
                    <div className="grid grid-cols-2 gap-6">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="space-y-3">
                          <div className="h-4 w-24 animate-pulse rounded bg-gray-100" />
                          <div className="grid grid-cols-7 gap-1">
                            {Array.from({ length: 28 }).map((__, j) => (
                              <div key={j} className="h-7 animate-pulse rounded-lg bg-gray-50" />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : isError ? (
                    <p className="text-sm text-destructive">{t('campaignCalendar.loadError')}</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-6">
                      {windowMonths.map(({ year, month }) => renderMonthGrid(year, month))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="lg:col-span-5">
            <Card
              className="bg-white border-0 shadow-sm rounded-[24px] overflow-hidden flex flex-col min-h-0 lg:self-start"
              style={
                isLgViewport && eventsPanelMaxHeightPx != null
                  ? { maxHeight: eventsPanelMaxHeightPx }
                  : undefined
              }
            >
              <CardHeader className="px-8 pt-8 pb-4 shrink-0">
                <CardTitle className="text-[18px] font-bold text-[#101828]">
                  {t('campaignCalendar.upcomingEvents')}
                  {selectedCampaign !== 'all' && selectedFilterName && (
                    <span className="ml-2 text-[13px] text-[#667085]" style={{ fontWeight: 500 }}>
                      {t('campaignCalendar.filteredBy', { name: selectedFilterName })}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-8 pb-8 pt-0 flex flex-1 min-h-0 flex-col overflow-hidden">
                <div
                  ref={eventsScrollRef}
                  className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden space-y-4 pr-1 [scrollbar-gutter:stable]"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f3f4f6' }}
                >
                  {showLoading ? (
                    <div className="flex min-h-[12rem] flex-1 flex-col gap-3 py-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-24 animate-pulse rounded-[20px] bg-gray-100" />
                      ))}
                    </div>
                  ) : isError ? (
                    <div className="flex min-h-[12rem] flex-1 items-center justify-center">
                      <p className="text-sm text-destructive text-center px-4">{t('campaignCalendar.loadError')}</p>
                    </div>
                  ) : sortedDates.length === 0 ? (
                    <div className="flex min-h-[12rem] flex-1 items-center justify-center">
                      <div className="text-center py-8">
                        <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" aria-hidden />
                        <p className="text-[14px] text-[#667085]">{t('campaignCalendar.emptyTitle')}</p>
                        <p className="text-[12px] text-[#98a2b3] mt-1">{t('campaignCalendar.emptyHint')}</p>
                      </div>
                    </div>
                  ) : (
                    sortedDates.map((date) => {
                      const dayEvents = eventsByDate[date];
                      const dayGroups = groupedEventsByDate[date] ?? [];
                      const dayDistance = getDayDistance(date);
                      return (
                        <div key={date} data-date={date} className="bg-[#f8f9fa] rounded-[20px] p-5 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0">
                              <CalendarIcon className="w-6 h-6 text-[#be43ff]" aria-hidden />
                            </div>
                            <div>
                              <h4 className="text-[14px] font-bold text-[#101828]">{dayDistance}</h4>
                              <p className="text-[12px] text-[#667085]">
                                {dayEvents.length === 1
                                  ? t('campaignCalendar.eventCountOne', { date, count: dayEvents.length })
                                  : t('campaignCalendar.eventCountOther', { date, count: dayEvents.length })}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2 pl-1">
                            {dayGroups.map((group) => {
                              const event = group.representative;
                              const eventHref = getHrefForCalendarEvent(event);
                              const cardAria = getCalendarEventCardAriaLabel(event, t);
                              const isNavigable = Boolean(eventHref);
                              return (
                                <div
                                  key={group.key}
                                  role={isNavigable ? 'button' : undefined}
                                  tabIndex={isNavigable ? 0 : undefined}
                                  aria-label={cardAria ?? undefined}
                                  className={cn(
                                    'bg-white p-4 rounded-xl border transition-shadow',
                                    isNavigable &&
                                      'cursor-pointer hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/40 focus-visible:ring-offset-2',
                                    !isNavigable && 'cursor-default',
                                  )}
                                  style={{
                                    backgroundColor: hexWithAlpha(event.accentColor, '14'),
                                    borderColor: hexWithAlpha(event.accentColor, '33'),
                                  }}
                                  onClick={() => {
                                    if (eventHref) navigate(eventHref);
                                  }}
                                  onKeyDown={(e) => {
                                    if (!eventHref) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      navigate(eventHref);
                                    }
                                  }}
                                >
                                  <div className="flex items-start gap-3">
                                    {event.timeLabel && (
                                      <span className="text-[12px] font-bold text-[#101828] shrink-0">
                                        {event.timeLabel}
                                      </span>
                                    )}
                                    <div className={cn('min-w-0 flex-1 space-y-1', !event.timeLabel && 'pl-0')}>
                                      <div className="flex items-start justify-between gap-2">
                                        <p
                                          className="min-w-0 flex-1 text-left text-[12px] text-gray-600"
                                          style={{ fontWeight: 500 }}
                                        >
                                          {event.title}
                                        </p>
                                        {group.collaborations.length === 0 && group.count > 1 && (
                                          <span
                                            className="inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-[#be43ff] px-1.5 text-[10px] font-bold text-white"
                                            aria-label={t('campaignCalendar.groupedEventsBadgeAria', {
                                              count: group.count,
                                            })}
                                          >
                                            {group.count}
                                          </span>
                                        )}
                                      </div>
                                      {event.beat_name && (
                                        <p className="text-[11px] text-[#98a2b3] text-left">{event.beat_name}</p>
                                      )}
                                      {group.collaborations.length > 0 ? (
                                        <ul
                                          className="max-h-48 space-y-1.5 overflow-y-auto pt-1 pr-0.5 [scrollbar-gutter:stable]"
                                          style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f3f4f6' }}
                                        >
                                          {group.collaborations.map((collab) => (
                                            <li
                                              key={collab.collaboration_id}
                                              className="flex items-center justify-between gap-2 pt-0.5"
                                            >
                                              <div className="flex min-w-0 flex-1 items-center gap-2">
                                                <div className="h-6 w-6 shrink-0 overflow-hidden rounded-full bg-gray-100">
                                                  {collab.creator_mugshot_url ? (
                                                    <ImageWithFallback
                                                      src={collab.creator_mugshot_url}
                                                      alt={
                                                        collab.creator_name?.trim()
                                                          ? t('campaignCalendar.creatorMugshotAlt', {
                                                              name: collab.creator_name.trim(),
                                                            })
                                                          : t('campaignCalendar.creatorMugshotAltFallback')
                                                      }
                                                      className="h-full w-full object-cover"
                                                    />
                                                  ) : (
                                                    <div className="flex h-full w-full items-center justify-center text-[10px] font-bold text-[#667085]">
                                                      {creatorInitials(collab.creator_name, collab.creator_handle)}
                                                    </div>
                                                  )}
                                                </div>
                                                <span className="truncate text-[11px] text-[#667085]">
                                                  {[
                                                    collab.creator_name?.trim(),
                                                    collab.creator_handle
                                                      ? `@${collab.creator_handle.replace(/^@/, '')}`
                                                      : '',
                                                  ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                                </span>
                                              </div>
                                              {collab.count > 1 && (
                                                <span
                                                  className="inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-[#be43ff] px-1.5 text-[10px] font-bold text-white"
                                                  aria-label={t('campaignCalendar.groupedEventsBadgeAria', {
                                                    count: collab.count,
                                                  })}
                                                >
                                                  {collab.count}
                                                </span>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        event.collaboration_id && (
                                          <div className="flex items-center gap-2 pt-0.5">
                                            <div className="h-6 w-6 shrink-0 overflow-hidden rounded-full bg-gray-100">
                                              {event.creator_mugshot_url ? (
                                                <ImageWithFallback
                                                  src={event.creator_mugshot_url}
                                                  alt={
                                                    event.creator_name?.trim()
                                                      ? t('campaignCalendar.creatorMugshotAlt', {
                                                          name: event.creator_name.trim(),
                                                        })
                                                      : t('campaignCalendar.creatorMugshotAltFallback')
                                                  }
                                                  className="h-full w-full object-cover"
                                                />
                                              ) : (
                                                <div className="flex h-full w-full items-center justify-center text-[10px] font-bold text-[#667085]">
                                                  {creatorInitials(event.creator_name, event.creator_handle)}
                                                </div>
                                              )}
                                            </div>
                                            <span className="truncate text-[11px] text-[#667085]">
                                              {[
                                                event.creator_name?.trim(),
                                                event.creator_handle
                                                  ? `@${event.creator_handle.replace(/^@/, '')}`
                                                  : '',
                                              ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            </span>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
